import type { StateContext } from '@ngxs/store';
import { Action, createSelector, type NgxsOnInit, Selector, State } from '@ngxs/store';
import { inject, Injectable, NgZone } from '@angular/core';
import type { SessionStateModel } from './session-state.model';
import { SESSION_STATE_TOKEN } from './session-state.token';
import { SessionMessageProcessingCardCloseAction } from './action/message/session-message-processing-card-close.action';
import { SessionMessageProcessingCardOpenAction } from './action/message/session-message-processing-card-open.action';
import { SessionMessageProcessingTaskMessageAction } from './action/message/session-message-processing-task-message.action';
import { SessionHandleMessageAction } from './action/message/session-handle-message.action';
import { SessionConnectingAction } from './action/connection/session-connecting.action';
import { SessionConnectedAction } from './action/connection/session-connected.action';
import { produce } from 'immer';
import type { SessionCard, SessionTask } from './type';
import { SessionTaskProcessingTaskStepAction } from './action/task/session-task-processing-task-step.action';
import { SessionTaskProcessingTaskProgressAction } from './action/task/session-task-processing-task-progress.action';
import { SessionTaskProcessingTaskSuccessAction } from './action/task/session-task-processing-task-success.action';
import { SessionTaskProcessingTaskFailureAction } from './action/task/session-task-processing-task-failure.action';
import { getCardById } from './helper/get-card-by-id';
import { findTaskById } from './helper/find-task-by-id';
import { getTaskById } from './helper/get-task-by-id';
import { getTaskByCardAndTaskId } from './helper/get-task-by-card-and-task-id';
import { immutableSetState } from './helper/immutable-set-state';
import {
  CardOpen,
  OpenSessionRequest,
  SessionMessage,
  SessionServiceClient,
  TaskMessage,
} from '@camino-solutions/share/proto/common/services';
// import { SessionTaskHandleTaskInformationAction } from './action/task/session-task-handle-task-informaction.action';
// import { hasCardDuplicatedTaskById } from './helper/has-card-duplicated-task-by-id';
import { asapScheduler, retry, Subject, switchMap, takeUntil, tap, timer } from 'rxjs';
import { AuthService } from '@camino-solutions/core/auth/service';
import { SessionReConnectingAction } from './action/connection/session-re-connecting.action';
import { SessionCardRemoveAction } from './action/card/session-card-remove.action';

type SessionStateContext = StateContext<SessionStateModel>;

// TODO default
// produce(
//   {} as AppStateModel,
//   () =>
//     ({
//       inited: false,
//       isLoggedIn: false,
//       showSplash: false,
//       loading: true,
//       idle: false,
//       theme: 'light',
//       menu: {
//         anchored: false,
//       },
//     }) satisfies AppStateModel,
// )

/**
 * TODO asapScheduler => https://github.com/ngxs/store/issues/839
 * Hol tart az asapScheduler para => https://github.com/ngxs/store/issues/139
 */
@State<SessionStateModel>({
  name: SESSION_STATE_TOKEN,
  defaults: produce({} as SessionStateModel, () => ({
    connectionState: 'disconnected',
    cards: [],
  })),
})
@Injectable()
export class SessionState implements NgxsOnInit {
  readonly #ngZone = inject(NgZone);
  readonly #authService = inject(AuthService);
  readonly #sessionServiceClient = inject(SessionServiceClient);
  readonly #emergencyStop$ = new Subject<void>();

  ngxsOnInit({ dispatch }: SessionStateContext): void {
    dispatch(new SessionConnectingAction());
  }

  @Selector([SESSION_STATE_TOKEN])
  static connectionState({
    connectionState,
  }: SessionStateModel): SessionStateModel['connectionState'] {
    return connectionState ?? 'disconnected';
  }

  static getCards(/* TODO @koni-val megbeszelni milyen filterek kellenek */) {
    return createSelector([SESSION_STATE_TOKEN], ({ cards }: SessionStateModel) => {
      // Deduplikáljuk a kártyákat ID alapján, hogy biztosan ne legyenek duplikátumok
      const uniqueCards = cards.filter(
        (card, index, self) => index === self.findIndex(c => c.id === card.id),
      );
      return uniqueCards;
    });
  }

  static getCardTasks(id: string) {
    /**
     * Szandekosan adunk vissza ures tombot, igy hamarabb kapjuk vissza a card id-t mintsem hogy task lenne rajta, a feluletnek akkor sem kell vizsgalnia
     */
    return createSelector(
      [SESSION_STATE_TOKEN],
      ({ cards }: SessionStateModel): SessionTask[] =>
        getCardById(id, cards, 'getCardTasks')?.tasks ?? [],
    );
  }

  static getCardById(id: SessionCard['id']) {
    return createSelector([SESSION_STATE_TOKEN], ({ cards }: SessionStateModel) =>
      getCardById(id, cards, 'getCardById'),
    );
  }

  static findTaskById(id: SessionTask['id']) {
    return createSelector([SESSION_STATE_TOKEN], ({ cards }: SessionStateModel) =>
      findTaskById(id, cards, 'findTaskById'),
    );
  }

  static getTaskById(id: SessionTask['id'], cardId: SessionCard['id']) {
    return createSelector([SESSION_STATE_TOKEN], ({ cards }: SessionStateModel) =>
      getTaskById(id, getCardById(cardId, cards, 'getTaskById')?.tasks ?? [], 'getTaskById'),
    );
  }

  static getTaskByCardAndTaskId(cardId: SessionTask['id'], taskId: SessionCard['id']) {
    return createSelector([SESSION_STATE_TOKEN], ({ cards }: SessionStateModel) =>
      getTaskByCardAndTaskId(cardId, taskId, cards, 'getTaskByCardAndTaskId'),
    );
  }

  // TODO Provide https://www.ngxs.io/concepts/state/life-cycle
  /**
   * Elinditjuk a session-t.
   * Ha hiba tortenik akkor automatikusan ujra csatlakozzunk.
   * Ha uj uzenet jon akkor delegaljuk a `SessionHandleMessageAction` -ben
   */
  @Action(SessionConnectingAction)
  sessionConnecting({ dispatch }: SessionStateContext) {
    this.#ngZone.runOutsideAngular(() => {
      this.#authService.isAuthenticatedAndIsDoneLoading$
        .pipe(
          switchMap(() =>
            // TODO session visszakotese!
            this.#sessionServiceClient.openSession(new OpenSessionRequest()).pipe(
              // of({} as any).pipe(
              takeUntil(this.#emergencyStop$),
              retry({
                count: 100,
                resetOnSuccess: true,
                delay: (error, retryCount) =>
                  timer(1000 * retryCount).pipe(
                    tap(() => this.#ngZone.run(() => dispatch(new SessionReConnectingAction()))),
                  ),
              }),
              tap(() => this.#ngZone.run(() => dispatch(new SessionConnectedAction()))),
            ),
          ),
        )
        .subscribe(message => {
          this.#ngZone.run(() => {
            // console.log('new session message:', message.toProtobufJSON());
            if (message.control?.closeCurrentSession === true) {
              console.info('EMERGENCY STOP SESSION');
              this.#emergencyStop$.next();
              this.#emergencyStop$.complete();
              this.#authService.logout();
            }
            // A stream -en kapott uzenetett eldobjuk hogy mindenki fel tudja dolgozni akinek van vele dolga
            dispatch(new SessionHandleMessageAction(message));
          });
        });
    });
  }

  /**
   * Uj `TaskInformation` feldolgozasa, jellemzoen a backend akciok vegen kapjuk vissza
   *
   * Jelenleg csak a kartyahoz rendeljuk az uj ures task-t, de semmi mast nem csinalunk
   */
  // @Action(SessionTaskHandleTaskInformationAction)
  // sessionTaskHandleTaskInformation(
  //   { setState, getState }: SessionStateContext,
  //   { taskInformation: { cardId, taskId } }: SessionTaskHandleTaskInformationAction,
  // ) {
  //   if (
  //     hasCardDuplicatedTaskById(
  //       cardId,
  //       taskId,
  //       getState().cards,
  //       'sessionTaskHandleTaskInformation',
  //     ) === false
  //   ) {
  //     immutableSetState(setState, draft => {
  //       // TODO ez most hidden task a closed = undefined
  //       // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  //       getCardById(cardId, draft.cards, 'sessionTaskHandleTaskInformation')!.tasks!.push({
  //         type: 'opened',
  //         id: taskId,
  //         description: '',
  //         progress: undefined,
  //         error: undefined,
  //         action: undefined,
  //       });
  //     });
  //   }
  // }

  /**
   * Uj message feldolgozasa
   */
  @Action(SessionHandleMessageAction)
  sessionHandleMessage(
    { dispatch }: SessionStateContext,
    { sessionMessage: { command, cardOpen, cardClose, task } }: SessionHandleMessageAction,
  ) {
    switch (command) {
      /**
       * Non-null engedelyezett, mert a proto altal biztositva kell lenni hogy az adott commandhoz
       * message fel van toltve.
       */
      case SessionMessage.CommandCase.cardOpen:
        asapScheduler.schedule(() =>
          dispatch(new SessionMessageProcessingCardOpenAction(cardOpen!)),
        );
        break;
      case SessionMessage.CommandCase.cardClose:
        asapScheduler.schedule(() =>
          dispatch(new SessionMessageProcessingCardCloseAction(cardClose!)),
        );
        break;
      case SessionMessage.CommandCase.task:
        asapScheduler.schedule(() =>
          dispatch(new SessionMessageProcessingTaskMessageAction(task!)),
        );
        break;
    }
  }

  /**
   * Message feladat feldolgozasa: CardOpen
   */
  @Action(SessionMessageProcessingCardOpenAction)
  sessionMessageProcessingCardOpen(
    { setState, getState }: SessionStateContext,
    {
      cardOpen: { cardId: id, title, subtitle, scenario, success, failure },
    }: SessionMessageProcessingCardOpenAction,
  ) {
    // Ellenőrizzük, hogy már létezik-e kártya ugyanazzal az ID-val
    const existingCard = getCardById(id, getState().cards, 'sessionMessageProcessingCardOpen');
    if (existingCard) {
      console.warn(`Card with ID ${id} already exists, skipping duplicate.`);
      return;
    }

    const sessionCard: SessionCard = {
      type: 'opened',
      id,
      title,
      subtitle,
      tasks: [],
      meta: undefined,
      error: undefined,
    };

    if (scenario === CardOpen.ScenarioCase.failure) {
      sessionCard.type = 'start-with-error';
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      sessionCard.error = failure!.error;
    } else {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      sessionCard.meta = success!;
    }

    immutableSetState(setState, draft => {
      draft.cards.push(sessionCard);
    });
  }

  /**
   * Message feladat feldolgozasa: CardClose
   */
  @Action(SessionMessageProcessingCardCloseAction)
  sessionMessageProcessingCardClose(
    { setState }: SessionStateContext,
    { cardClose: { cardId, failure } }: SessionMessageProcessingCardCloseAction,
  ) {
    immutableSetState(setState, draft => {
      const card = getCardById(
        cardId,
        draft.cards as SessionCard[],
        'sessionMessageProcessingCardClose',
      );

      if (card !== undefined) {
        if (failure !== undefined) {
          card.type = 'closed-with-error';
          card.error = failure.error;
        } else {
          card.type = 'closed';
        }
      }
    });
  }

  /**
   * Kárty törlése
   */
  @Action(SessionCardRemoveAction)
  SessionCardRemove({ setState }: SessionStateContext, { cardId }: SessionCardRemoveAction) {
    immutableSetState(setState, draft => {
      const cardIndex = draft.cards.findIndex(card => card.id === cardId);

      if (cardIndex !== -1) {
        draft.cards.splice(cardIndex, 1);
      }
    });
  }

  /**
   * Message feladat feldolgozasa: TaskMessage
   */
  @Action(SessionMessageProcessingTaskMessageAction)
  sessionMessageProcessingTaskMessage(
    { dispatch }: SessionStateContext,
    {
      taskMessage: { cardId, event, progress, success, failure, step },
    }: SessionMessageProcessingTaskMessageAction,
  ) {
    switch (event) {
      case TaskMessage.EventCase.progress:
        asapScheduler.schedule(() =>
          dispatch(new SessionTaskProcessingTaskProgressAction(cardId, progress!)),
        );
        break;
      case TaskMessage.EventCase.success:
        asapScheduler.schedule(() =>
          dispatch(new SessionTaskProcessingTaskSuccessAction(cardId, success!)),
        );
        break;
      case TaskMessage.EventCase.failure:
        asapScheduler.schedule(() =>
          dispatch(new SessionTaskProcessingTaskFailureAction(cardId, failure!)),
        );
        break;
      case TaskMessage.EventCase.step:
        asapScheduler.schedule(() =>
          dispatch(new SessionTaskProcessingTaskStepAction(cardId, step!)),
        );
        break;
    }
  }

  /**
   * Task feladat feldolgozasa: TaskProgress
   */
  @Action(SessionTaskProcessingTaskProgressAction)
  sessionTaskProcessingTaskProgress(
    { setState }: SessionStateContext,
    { cardId, taskProgress: { taskId, progressRatio } }: SessionTaskProcessingTaskProgressAction,
  ) {
    immutableSetState(setState, draft => {
      let task = getTaskByCardAndTaskId(
        cardId,
        taskId,
        draft.cards,
        'sessionTaskProcessingTaskProgress',
      );
      if (task === undefined) {
        // Ha nem letezik a task akkor letrehozzuk es a kartyahoz rendeljuk
        const card = getCardById(cardId, draft.cards, 'sessionTaskProcessingTaskProgress');
        if (card === undefined) {
          throw new Error(
            `[SessionState::sessionTaskProcessingTaskProgress] Cannot find card ${cardId}`,
          );
        }
        task = {
          type: 'opened',
          id: taskId,
          description: '',
          progress: undefined,
          action: undefined,
          error: undefined,
        };
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        card.tasks!.push(task);
      }
      task.progress = progressRatio;
    });
  }

  /**
   * Task feladat feldolgozasa: TaskSuccess
   */
  @Action(SessionTaskProcessingTaskSuccessAction)
  sessionTaskProcessingTaskSuccess(
    { setState }: SessionStateContext,
    {
      cardId,
      taskSuccess: { taskId, result, download, confirm, data, navigate },
    }: SessionTaskProcessingTaskSuccessAction,
  ) {
    immutableSetState(setState, draft => {
      let task = findTaskById(taskId, draft.cards, 'sessionTaskProcessingTaskSuccess');
      if (task === undefined) {
        // Ha nem letezik a task akkor letrehozzuk es a kartyahoz rendeljuk
        const card = getCardById(cardId, draft.cards, 'sessionTaskProcessingTaskSuccess');
        if (card === undefined) {
          throw new Error(
            `[SessionState::sessionTaskProcessingTaskSuccess] Cannot find card ${cardId}`,
          );
        }

        task = {
          type: 'opened',
          id: taskId,
          description: '',
          progress: undefined,
          action: undefined,
          error: undefined,
        };
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        card.tasks!.push(task);
      }
      task.type = 'closed';
      task.action = {
        resultCase: result,
        download,
        confirmation: confirm,
        data,
        navigate,
      };
    });
  }

  /**
   * Task feladat feldolgozasa: TaskFailure
   */
  @Action(SessionTaskProcessingTaskFailureAction)
  sessionTaskProcessingTaskFailure(
    { setState }: SessionStateContext,
    { cardId, taskFailure: { taskId, message, severity } }: SessionTaskProcessingTaskFailureAction,
  ) {
    immutableSetState(setState, draft => {
      let task = findTaskById(taskId, draft.cards, 'sessionTaskProcessingTaskFailure');
      if (task === undefined) {
        const card = getCardById(cardId, draft.cards, 'sessionTaskProcessingTaskFailure');
        if (card === undefined) {
          throw new Error(
            `[SessionState::sessionTaskProcessingTaskFailure] Cannot find card ${cardId}`,
          );
        }
        task = {
          type: 'opened',
          id: taskId,
          description: '',
          progress: undefined,
          action: undefined,
          error: undefined,
        };
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        card.tasks!.push(task);
      }
      task.type = 'closed-with-error';
      task.error = {
        message,
        severity,
      };
    });
  }

  /**
   * Task feladat feldolgozasa: TaskStep
   */
  @Action(SessionTaskProcessingTaskStepAction)
  sessionTaskProcessingTaskStep(
    { setState }: SessionStateContext,
    { cardId, taskStep: { taskId, stepDescription } }: SessionTaskProcessingTaskStepAction,
  ) {
    immutableSetState(setState, draft => {
      let task = findTaskById(taskId, draft.cards, 'sessionTaskProcessingTaskStep');
      if (task === undefined) {
        const card = getCardById(cardId, draft.cards, 'sessionTaskProcessingTaskStep');
        if (card === undefined) {
          throw new Error(
            `[SessionState::sessionTaskProcessingTaskStep] Cannot find card ${cardId}`,
          );
        }
        task = {
          type: 'opened',
          id: taskId,
          description: '',
          progress: undefined,
          action: undefined,
          error: undefined,
        };
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        card.tasks!.push(task);
      }
      task.description = stepDescription;
    });
  }
}
